import { ReactNode } from 'react';
import cn from 'classnames';
import Link from 'next/link';
// Types
import { CardSize } from '../BaseCard/BaseCard';
// UI
import Text, { TextVariants } from 'ui3/Text/Text';
// Helpers
import { truncateTextAtWord } from 'helpers/string';
// Styles
import styles from './CardDescription.module.scss';

export type BasicCardDescriptionProps = {
  title?: string;
  user?: string;
  date?: string;
  size?: CardSize;
  userUrl?: string;
  showTitle?: boolean;
  showUser?: boolean;
  showDate?: boolean;
  userLineClamp?: number;
  titleMaxLength?: number; // Max characters for title before truncation
  //showAvatar?: boolean; // TODO: check if this is needed
};

export type CardDescriptionProps = React.ComponentPropsWithoutRef<'p'> &
  BasicCardDescriptionProps;

const sizeMap = {
  small: {
    title: 'subtitle3',
    user: 'body3Regular',
    date: 'body3Regular',
  },
  medium: {
    title: 'subtitle2',
    user: 'body2Regular',
    date: 'body3Regular',
  },
  large: {
    title: 'subtitle1',
    user: 'body1Regular16',
    date: 'body2Regular',
  },
};

const WithLink = ({ link, children }: { link?: string; children: ReactNode }) =>
  link ? (
    <Link href={link} prefetch={false}>
      <a href={link}>{children}</a>
    </Link>
  ) : (
    <>{children}</>
  );

const CardDescription: React.FC<
  CardDescriptionProps & { children?: ReactNode }
> = ({
  size,
  title,
  user,
  date,
  userUrl,
  showTitle = true,
  showUser = true,
  showDate = true,
  userLineClamp = 1,
  titleMaxLength = 50,
  className,
  children,
  ...rest
}) => {
  const sizeClass = size ?? 'large';
  const userLineClampClass = `lineClamp${userLineClamp}` as keyof typeof styles;

  const truncatedTitle =
    title && titleMaxLength ? truncateTextAtWord(title, titleMaxLength) : title;

  return (
    <div className={cn(styles.root)}>
      {showTitle && title ? (
        <Text
          as="h3"
          className={cn(styles.title, className, {
            [styles.titleText]: !size,
          })}
          variant={size ? (sizeMap[sizeClass].title as TextVariants) : null}
          title={title}
          {...rest}
        >
          {truncatedTitle}
        </Text>
      ) : null}
      {showUser && user ? (
        <WithLink link={userUrl}>
          <Text
            color="lights-low"
            className={cn(
              userLineClamp === 1 ? null : styles.overflow,
              userLineClamp === 1 ? null : styles[userLineClampClass],
              className,
              {
                [styles.user]: !size || userLineClamp === 1,
              }
            )}
            variant={size ? (sizeMap[sizeClass].user as TextVariants) : null}
            title={user}
            {...rest}
          >
            {user}
          </Text>
        </WithLink>
      ) : null}
      {showDate && date ? (
        <Text
          color="lights-low"
          className={cn(styles.overflow, className, {
            [styles.date]: !size,
          })}
          variant={size ? (sizeMap[sizeClass].date as TextVariants) : null}
          title={date}
          {...rest}
        >
          {date}
        </Text>
      ) : null}
      {children}
    </div>
  );
};

export default CardDescription;
